/**
 * PMP Tab Component
 *
 * Displays the current PMP (Prix Moyen Pondere) and its history for a raw product.
 * @see US-9.8 - Widget PMP dans Drawer Produit
 */

import { TrendingUp, TrendingDown, Minus, ExternalLink, AlertCircle } from 'lucide-react';
import {
  Badge,
  Skeleton,
  EmptyState,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui';
import { usePMP, usePMPHistory } from '@/features/goods-receipts';
import { formatCentimes } from '@/lib/format';
import { useNavigate } from 'react-router-dom';
import { useCurrency, useFormatDate } from '@/hooks';

interface PMPTabProps {
  productId: string;
  recipeUnitSymbol: string;
}

export function PMPTab({ productId, recipeUnitSymbol }: PMPTabProps) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data: pmpData, isLoading: pmpLoading, error: pmpError } = usePMP(productId);
  const { data: historyData, isLoading: historyLoading } = usePMPHistory(productId, { limit: 5 });

  const history = historyData?.data ?? [];

  const handleViewReceipt = (receiptId: string | null) => {
    if (receiptId) {
      navigate(`/achats/receptions/${receiptId}`);
    }
  };

  if (pmpLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (pmpError) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Erreur lors du chargement"
        description={pmpError.message}
      />
    );
  }

  // No PMP available
  if (!pmpData || pmpData.currentPmp === null) {
    // Display unit: kg for mass (g, mg), L for volume (mL, cL), otherwise as-is
    const displayUnit = recipeUnitSymbol === 'g' || recipeUnitSymbol === 'mg' ? 'kg' : recipeUnitSymbol === 'mL' || recipeUnitSymbol === 'cL' ? 'L' : recipeUnitSymbol;
    return (
      <EmptyState
        icon={TrendingUp}
        title="PMP non disponible"
        description={`Ce produit n'a pas encore ete recu. Le PMP sera calcule automatiquement lors de la premiere reception validee et exprime en ${currency}/${displayUnit}.`}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Current PMP Card */}
      <Card>
        <CardHeader className="pb-2">
          <span className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
            PMP Actuel
          </span>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between">
            {/* Main PMP value */}
            <div>
              <div className="text-3xl font-bold text-dark-500">
                {pmpData.currentPmpValue !== null
                  ? formatCentimes(pmpData.currentPmpValue, currency)
                  : '-'}
              </div>
              <div className="text-sm text-neutral-500 mt-1">
                par {pmpData.displayUnitSymbol ?? recipeUnitSymbol}
              </div>
            </div>

            {/* Variation Badge */}
            {pmpData.variation.percent !== null && pmpData.variation.direction && (
              <Badge
                variant={
                  pmpData.variation.direction === 'up' ? 'danger' :
                  pmpData.variation.direction === 'down' ? 'success' : 'neutral'
                }
              >
                {pmpData.variation.direction === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
                {pmpData.variation.direction === 'down' && <TrendingDown className="w-3 h-3 mr-1" />}
                {pmpData.variation.direction === 'stable' && <Minus className="w-3 h-3 mr-1" />}
                {pmpData.variation.percent > 0 ? '+' : ''}{pmpData.variation.percent.toFixed(1)}%
              </Badge>
            )}
          </div>

          {/* Metadata */}
          <div className="mt-6 pt-4 border-t border-neutral-100 space-y-2">
            {pmpData.lastUpdated && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Derniere MAJ</span>
                <span className="text-neutral-700">{formatDateShort(pmpData.lastUpdated)}</span>
              </div>
            )}
            {pmpData.sourceLabel && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Source</span>
                <button
                  onClick={() => handleViewReceipt(pmpData.sourceId)}
                  className="text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  {pmpData.sourceLabel}
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="border-b border-neutral-200 bg-neutral-50">
            <span className="text-sm font-medium text-neutral-700">Historique PMP</span>
          </CardHeader>
          {historyLoading ? (
            <Skeleton className="h-48" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">PMP</TableHead>
                  <TableHead className="text-center">Variation</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-neutral-600">
                      {formatDateShort(item.validFrom)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCentimes(item.pmpValue, currency)}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.variation !== null && item.variationDirection ? (
                        <Badge
                          variant={
                            item.variationDirection === 'up' ? 'danger' :
                            item.variationDirection === 'down' ? 'success' : 'neutral'
                          }
                        >
                          {item.variationDirection === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
                          {item.variationDirection === 'down' && <TrendingDown className="w-3 h-3 mr-1" />}
                          {item.variationDirection === 'stable' && <Minus className="w-3 h-3 mr-1" />}
                          {item.variation > 0 ? '+' : ''}{item.variation.toFixed(1)}%
                        </Badge>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.sourceLabel ? (
                        <button
                          onClick={() => handleViewReceipt(item.sourceId)}
                          className="text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                          {item.sourceLabel}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-neutral-400">{item.sourceType}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
